import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Button,
  LinearProgress,
  List,
  ListItemButton,
  Divider,
  Paper,
  Card,
  CardContent,
  Chip,
  Avatar,
  CircularProgress,
  IconButton,
  Tooltip,
  Fade,
  Stack,
  useTheme,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  TrendingUp,
  Assignment,
  Notifications,
  FormatListBulleted as ActionIcon,
  EmojiEvents as HouseIcon,
  CalendarToday,
  Timer,
  PlayCircleOutline as VideoIcon,
  Quiz as TestIcon,
  QuizOutlined as MockExamIcon,
  ChevronRight,
  MoreVert as MoreIcon,
  Warning as WarningIcon,
  ShoppingBag as ShoppingBagIcon,
  Article as ArticleIcon,
  FileDownloadOutlined as DownloadIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import AnnouncementsList from "../../components/AnnouncementsList";
import MobileStudentDashboard from "./MobileStudentDashboard";
import useStudentPortalMode, { setTesterDashboardView } from "../../hooks/useStudentPortalMode";
import api from "../../api";
import { toast } from "react-toastify";
import { sortHierarchyItems } from "../../utils/hierarchyOrdering";
import DashboardShopCard from "./components/DashboardShopCard";
import NoPlanDashboard from "./components/NoPlanDashboard";
import GettingStartedCard from "../../components/GettingStartedCard";
import PackDownloadsDialog from "./components/PackDownloadsDialog";
import { completePanelPurchase, PANEL_PURCHASE_MESSAGES } from "../../utils/panelPurchase";
import { formatTrialEnd } from "../../utils/mobileSubscriptionFormat";
import { friendlyPackName, splitPackName } from "../../utils/packWording";

// --- Mock Data for missing backend pieces ---
const MOCK_HOUSE_STANDINGS = [
  { name: "House Alpha", points: 1250, color: "#667eea", avatar: "A" },
  { name: "House Beta", points: 1120, color: "#764ba2", avatar: "B" },
  { name: "House Gamma", points: 980, color: "#4caf50", avatar: "G" },
  { name: "House Delta", points: 850, color: "#ff9800", avatar: "D" },
];

const MOCK_ACTION_ITEMS = [];

const CourseDashboard = ({ subscriptionLock = null }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [urgentAnnouncements, setUrgentAnnouncements] = useState([]);
  const [myGameScore, setMyGameScore] = useState(null);
  const navigate = useNavigate();
  const studentPrefix = process.env.REACT_APP_STUDENT_PREFIX || "student";

  // PDF packs are files, not a paper to sit, so they open their downloads instead.
  const [packDialog, setPackDialog] = useState(null);

  const handlePastPaperNavigate = (row) => {
    if (row.resource_type === "pdf_pack") {
      setPackDialog({ purchaseId: row.purchase_id, title: friendlyPackName(row.resource_name) });
      return;
    }
    const pid = row.purchase_id;
    if (pid != null && pid !== "") {
      navigate(`/${studentPrefix}/past-paper/${pid}/take`);
    } else {
      toast.info("Unable to open this practice paper. Try Practice Papers from the menu.");
    }
  };

  const handleLatestPurchaseNavigate = (row) => {
    if (row.resource_type === "mock_exam" && row.mock_exam_id != null) {
      navigate(
        `/${studentPrefix}/mock-exams?tab=exam&focusMockExam=${encodeURIComponent(row.mock_exam_id)}`
      );
      return;
    }
    if (row.resource_type === "course" && row.course_id != null) {
      navigate(`/${studentPrefix}/my-current-assignment`);
      return;
    }
    if (row.resource_type === "pdf_pack") {
      setPackDialog({ purchaseId: row.purchase_id, title: friendlyPackName(row.resource_name) });
      return;
    }
    if (row.resource_type === "paper") {
      const pid = row.purchase_id;
      if (pid != null && pid !== "") {
        navigate(`/${studentPrefix}/past-paper/${pid}/take`);
      } else {
        toast.info("Unable to open this practice paper. Try Practice Papers from the menu.");
      }
      return;
    }
  };

  // A pack bought from DashboardShopCard returns to this page (its return_url is
  // window.location.href), so this page has to record it — without this the purchase
  // waited on the Stripe webhook and the ?purchase=success&session_id= stayed in the
  // address bar. Same handling the two catalogue pages already do.
  useEffect(() => {
    let cancelled = false;
    completePanelPurchase()
      .then((outcome) => {
        if (cancelled || !outcome) return;
        const message = PANEL_PURCHASE_MESSAGES[outcome];
        if (outcome === "cancelled") toast.info(message);
        else if (message) toast.success(message);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch current user's game score (from leaderboard)
  useEffect(() => {
    const fetchMyGameScore = async () => {
      try {
        const res = await api.get("student/games/leaderboard", { params: { days: 0 } });
        const raw = res.data?.data?.leaderboard ?? res.data?.leaderboard ?? [];
        const list = Array.isArray(raw) ? raw : [];
        const userData = JSON.parse(localStorage.getItem("userData") || "{}");
        const myId = userData?.id ?? userData?.user_id;
        const me = list.find((row) => String(row.id) === String(myId));
        setMyGameScore(me ? Number(me.total_points ?? 0) : 0);
      } catch {
        setMyGameScore(0);
      }
    };
    fetchMyGameScore();
  }, []);

  // Fetch Dashboard Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get("student/dashboard");
        if (response.data && response.data.success) {
          setDashboardData(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Could not load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch urgent announcements (last 24 hours only)
  useEffect(() => {
    const fetchUrgentAnnouncements = async () => {
      try {
        const response = await api.get("student/announcements");
        if (response.data?.success && response.data?.data) {
          const list = Array.isArray(response.data.data) ? response.data.data : [];
          const now = new Date();
          const cutoff = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          const urgent = list.filter((a) => {
            if ((a.priority_level || "").toLowerCase() !== "urgent") return false;
            const created = a.created_at ? new Date(a.created_at) : null;
            return created && created >= cutoff;
          });
          setUrgentAnnouncements(urgent);
        } else {
          setUrgentAnnouncements([]);
        }
      } catch (err) {
        console.error("Error fetching urgent announcements:", err);
        setUrgentAnnouncements([]);
      }
    };
    fetchUrgentAnnouncements();
  }, []);

  const orderedSubjectBreakdown = useMemo(
    () => sortHierarchyItems(dashboardData?.subject_wise_breakdown || []),
    [dashboardData?.subject_wise_breakdown]
  );

  const latestNonPaperPurchases = useMemo(() => {
    return (dashboardData?.latest_purchases ?? [])
      .filter((r) => r.resource_type !== "paper" && r.resource_type !== "pdf_pack")
      .slice(0, 6);
  }, [dashboardData?.latest_purchases]);

  const recentPaperPurchases = useMemo(() => {
    const explicit = dashboardData?.recent_paper_purchases;
    if (Array.isArray(explicit)) {
      return explicit;
    }
    const legacy = (dashboardData?.latest_purchases ?? []).filter(
      (r) => r.resource_type === "paper" || r.resource_type === "pdf_pack"
    );
    return [...legacy]
      .sort((a, b) => {
        const ta = a.purchased_at ? new Date(a.purchased_at).getTime() : 0;
        const tb = b.purchased_at ? new Date(b.purchased_at).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 6);
  }, [dashboardData?.recent_paper_purchases, dashboardData?.latest_purchases]);
  const getNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const getCompletedAssignments = (subject) => {
    const direct = getNumber(subject?.completed_tests);
    if (direct > 0) return direct;
    return getNumber(subject?.completed_topic_tests) + getNumber(subject?.completed_subtopic_tests);
  };
  const getTotalAssignments = (subject) => {
    const direct = getNumber(subject?.total_tests);
    if (direct > 0) return direct;
    return getNumber(subject?.total_topic_tests) + getNumber(subject?.total_subtopic_tests);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress thickness={5} size={60} sx={{ color: '#764ba2' }} />
      </Box>
    );
  }

  const userData = JSON.parse(localStorage.getItem("userData") || "{}");

  // A lapsed plan lands the student here rather than on the mobile dashboard, so this is
  // where the way back to a plan has to live. `worksheetsLocked === false` is the API's
  // answer to "have they paid for anything?" (mock exams, past papers, packs or a course —
  // MobileSubscriptionAccessService::hasIndividualPurchases), and those stay theirs.
  // A running free trial is unlocked (it opens everything a plan does), so it must not be told
  // its plan has ended — say when the trial ends instead.
  const onTrial = subscriptionLock?.isTrial === true;
  const trialEndsLabel = formatTrialEnd(subscriptionLock?.trialEndsAt);
  const planEnded = subscriptionLock?.locked === true && !onTrial;
  const keepsPurchases = planEnded && subscriptionLock?.worksheetsLocked === false;
  // Their card was declined: they still own the plan, so the way back is a new card (the
  // choose-plan route shows the card form for this state), not a fresh plan choice.
  const cardFailed = planEnded && subscriptionLock?.recoveryAction === "payment_method_update";

  // On phones the button drops under the text; beside it, it squeezes the message into a sliver.
  const planBannerSx = {
    mb: 3,
    borderRadius: "16px",
    alignItems: "center",
    flexWrap: { xs: "wrap", sm: "nowrap" },
    "& .MuiAlert-message": { flex: 1, minWidth: 0 },
    "& .MuiAlert-action": {
      width: { xs: "100%", sm: "auto" },
      ml: { xs: 0, sm: "auto" },
      mr: { xs: 0, sm: "-8px" },
      pl: { xs: 0, sm: 2 },
      pt: 0,
      pb: { xs: 1, sm: 0 },
    },
  };
  const planBannerButtonSx = {
    textTransform: "none",
    fontWeight: 700,
    whiteSpace: "nowrap",
    width: { xs: "100%", sm: "auto" },
  };

  return (
    <Box sx={{ flexGrow: 1, pb: 4 }}>
      {onTrial ? (
        <Alert
          severity="success"
          sx={planBannerSx}
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${studentPrefix}/choose-plan?plans=1`)}
              sx={planBannerButtonSx}
            >
              Choose a plan
            </Button>
          }
        >
          <Typography sx={{ fontWeight: 700 }}>
            You're on a free trial{trialEndsLabel ? ` — it ends ${trialEndsLabel}` : ""}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            Worksheets, mock exams, practice papers and everything else are open during your trial. Choose a plan before it ends to keep access — when the trial ends, this opens on its own.
          </Typography>
        </Alert>
      ) : null}
      {planEnded ? (
        <Alert
          severity={cardFailed ? "warning" : "info"}
          sx={planBannerSx}
          action={
            <Button
              size="small"
              variant="contained"
              onClick={() => navigate(`/${studentPrefix}/choose-plan?plans=1`)}
              sx={planBannerButtonSx}
            >
              {cardFailed ? "Update payment details" : "Choose a plan"}
            </Button>
          }
        >
          <Typography sx={{ fontWeight: 700 }}>
            {cardFailed ? "Your plan payment failed." : "Your Mobile Plan has ended."}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            {cardFailed
              ? "We could not charge your card. Update your payment details and we will restart your plan at your usual price."
              : keepsPurchases
              ? "The mock exams, practice papers and courses you paid for are still yours — open them from the menu below. A plan adds worksheets, the wizard, flash cards and games."
              : "Choose a plan to unlock worksheets, the wizard, flash cards and games. You can also buy single mock exams and practice papers without one."}
          </Typography>
        </Alert>
      ) : null}

      {/* Top Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "#111827", mb: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Welcome back, {userData?.full_name?.split(" ")[0] || "Student"}! 👋
        </Typography>
        <Typography variant="body1" sx={{ color: "#374151" }}>
          Here's what's happening with your learning journey today.
        </Typography>
      </Box>

      <GettingStartedCard role="student" />

      {/* Navigation Shortcut Buttons */}
      <Grid container spacing={2} sx={{ mb: 4, flexWrap: { lg: "nowrap", xs: "wrap" } }}>
        {[
          { label: "Contents", icon: <Assignment />, color: "#4caf50", path: `/${studentPrefix}/my-current-assignment` },
          { label: "Mock exams", icon: <MockExamIcon />, color: "#7c3aed", path: `/${studentPrefix}/mock-exams?tab=exam` },
          { label: "Assignments", icon: <TestIcon />, color: "#ff9800", path: `/${studentPrefix}/my-course-test` },
          { label: "Videos", icon: <VideoIcon />, color: "#9c27b0", path: `/${studentPrefix}/my-course-video` },
          { label: "Classes", icon: <CalendarToday />, color: "#00acc1", path: `/${studentPrefix}/classes` },
          { label: "Announcements", icon: <Notifications />, color: "#ff5722", path: `/${studentPrefix}/announcements` },
        ].map((item) => (
          <Grid item xs={6} sm={4} md={2} key={item.label}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate(item.path)}
              sx={{
                py: 1,
                px: 1,
                minHeight: 56,
                borderRadius: "12px",
                background: "white",
                color: "#111827",
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: { xs: 13, sm: 14 },
                boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
                border: "1px solid #f0f0f0",
                textTransform: "none",
                fontWeight: 600,
                // Hover lift only where there is a pointer; on touch it sticks after a tap.
                "@media (hover: hover)": {
                  "&:hover": {
                    background: item.color,
                    color: "white",
                    transform: "translateY(-5px)",
                    boxShadow: `0 10px 25px ${item.color}30`,
                    borderColor: "transparent",
                    "& .MuiSvgIcon-root": { color: "white" },
                  },
                },
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Box sx={{
                color: item.color,
                transition: "color 0.3s",
                background: `${item.color}10`,
                p: 1,
                borderRadius: "12px",
                display: "flex",
                flexShrink: 0,
              }}>
                {item.icon}
              </Box>
              <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.label}
              </Box>
            </Button>
          </Grid>
        ))}
      </Grid>

      {urgentAnnouncements.length > 0 && (
        <Alert
          severity="error"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{
            mb: 2,
            "& .MuiAlert-message": { width: "100%" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {urgentAnnouncements.map((ann) => (
              <Box
                key={ann.id}
                component={RouterLink}
                to="/student/announcements"
                sx={{
                  py: 1,
                  px: 1.5,
                  bgcolor: "rgba(255,255,255,0.2)",
                  borderRadius: 1,
                  borderLeft: "4px solid #d32f2f",
                  textDecoration: "none",
                  color: "inherit",
                  cursor: "pointer",
                  display: "block",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {ann.title}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                  {ann.message?.length > 200 ? ann.message.slice(0, 200) + "…" : ann.message}
                </Typography>
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Left Section - Current Assignments */}
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment sx={{ color: "#4caf50" }} /> Current Assignments
            </Typography>
            <Button component={Link} to="/student/my-current-assignment" size="small" sx={{ fontWeight: 700 }}>View All</Button>
          </Box>

          <Stack spacing={2.5}>
            {orderedSubjectBreakdown.map((subject) => {
              const completedAssignments = getCompletedAssignments(subject);
              const totalAssignments = getTotalAssignments(subject);
              const remainingAssignments = Math.max(0, totalAssignments - completedAssignments);
              return (
              <Card
                key={subject.subject_id}
                sx={{
                  borderRadius: "24px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                  overflow: 'hidden',
                  border: '1px solid #f0f0f0',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#111827", mb: 0.5 }}>
                        {subject.subject_name} |

                        <Chip
                          size="small"
                          icon={<CalendarToday sx={{ fontSize: '12px !important' }} />}
                          label={`Week ${dashboardData.current_week_info?.week_name?.split(' ')[1] || '6'}`}
                          sx={{ fontWeight: 700, height: 24, fontSize: '11px', bgcolor: '#f1f2f6' }}
                        />
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#e74c3c', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Timer sx={{ fontSize: 14 }} />
                        {dashboardData.current_week_info?.end_date ? new Date(dashboardData.current_week_info.end_date).toLocaleDateString() : 'TBD'}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151' }}>Time Remaining : {dashboardData.current_week_info?.time_remaining}</Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151' }}>Weekly Progress</Typography>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827' }}>{Math.round(subject.content_completion_percentage)}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={subject.content_completion_percentage}
                      sx={{
                        height: 10,
                        borderRadius: 5,
                        bgcolor: '#f1f2f6',
                        '& .MuiLinearProgress-bar': {
                          background: `linear-gradient(90deg, #4caf50, #81c784)`
                        }
                      }}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                    <Grid item xs={4} sx={{ display: 'flex' }}>
                      <Box
                        component={Link}
                        to="/student/my-current-assignment"
                        state={{ subject_id: subject.subject_id, choose_title: 'TopicContent' }}
                        sx={{ p: 1.5, height: '100%', minHeight: 80, display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#f8f9fa', borderRadius: '16px', textAlign: 'center', border: '1px solid #eee', width: '100%', textDecoration: 'none', color: 'inherit', cursor: 'pointer', transition: 'background 0.2s', '&:hover': { bgcolor: '#e9ecef' } }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', fontSize: '1.2rem', lineHeight: 1.2 }}>
                          {subject.completed_topics}/{subject.total_topics}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600, fontSize: '0.7rem' }}>
                          Topics Done
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex' }}>
                      <Box
                        component={Link}
                        to="/student/my-current-assignment"
                        state={{ subject_id: subject.subject_id, choose_title: 'TopicTest' }}
                        sx={{ p: 1.5, height: '100%', minHeight: 80, display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#f8f9fa', borderRadius: '16px', textAlign: 'center', border: '1px solid #eee', width: '100%', textDecoration: 'none', color: 'inherit', cursor: 'pointer', transition: 'background 0.2s', '&:hover': { bgcolor: '#e9ecef' } }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', fontSize: '1.2rem', lineHeight: 1.2 }}>
                          {completedAssignments}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600, fontSize: '0.7rem' }}>
                          Assignments Completed
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4} sx={{ display: 'flex' }}>
                      <Box
                        component={Link}
                        to="/student/my-current-assignment"
                        state={{ subject_id: subject.subject_id, choose_title: 'TopicTest' }}
                        sx={{ p: 1.5, height: '100%', minHeight: 80, display: 'flex', flexDirection: 'column', justifyContent: 'center', bgcolor: '#f8f9fa', borderRadius: '16px', textAlign: 'center', border: '1px solid #eee', width: '100%', textDecoration: 'none', color: 'inherit', cursor: 'pointer', transition: 'background 0.2s', '&:hover': { bgcolor: '#e9ecef' } }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827', fontSize: '1.2rem', lineHeight: 1.2 }}>
                          {remainingAssignments}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#374151', fontWeight: 600, fontSize: '0.7rem' }}>
                          Remaining Assignments
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              );
            })}

            {!orderedSubjectBreakdown.length && (
              <Alert severity="info" sx={{ borderRadius: "20px", fontWeight: 600 }}>
                No active subject assignments found for this week.
              </Alert>
            )}
          </Stack>
        </Grid>

        {/* Middle Section - Performance Charts */}
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", mb: 2, display: 'flex', alignItems: 'left', gap: 1 }}>
            <TrendingUp sx={{ color: "#667eea" }} /> Weekly Performance Index
          </Typography>

          <Paper
            sx={{
              p: { xs: 2, sm: 2.5, md: 3.5 },
              borderRadius: "30px",
              boxShadow: "0 10px 40px rgba(102, 126, 234, 0.08)",
              mb: 4,
              background: "white",
              border: "1px solid #f0f0f0"
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#111827" }}>
                Score Analytics (%)
              </Typography>
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
                {dashboardData?.weekly_progress?.[0] && Object.keys(dashboardData.weekly_progress[0])
                  .filter(key => !['week_number', 'week_label', 'name', 'start_date', 'end_date', 'status'].includes(key))
                  .map((subject, idx) => (
                    <Box key={subject} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: idx === 0 ? '#667eea' : idx === 1 ? '#764ba2' : idx === 2 ? '#4caf50' : '#ff9800'
                      }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151' }}>{subject}</Typography>
                    </Box>
                  ))
                }
              </Stack>
            </Box>
            <Box sx={{ height: 300, width: '100%', position: 'relative' }}>
              {(!dashboardData?.weekly_progress?.length || !dashboardData?.weekly_progress?.[0] || Object.keys(dashboardData.weekly_progress[0]).filter(k => !['week_number', 'week_label', 'name', 'start_date', 'end_date', 'status'].includes(k)).length === 0) && (
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: '#374151', zIndex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Complete tests to see your subject-wise scores here</Typography>
                </Box>
              )}
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={dashboardData?.weekly_progress || []}
                  margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                >
                  <defs>
                    <linearGradient id="color0" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#667eea" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="color1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#764ba2" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#764ba2" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="color2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4caf50" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#4caf50" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="color3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ff9800" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#374151', fontSize: 12, fontWeight: 600 }}
                    dy={15}
                    label={{ value: 'Weeks', position: 'center', offset: 0, dy: 30, fill: '#374151', fontSize: 13, fontWeight: 700 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#374151', fontSize: 12, fontWeight: 600 }}
                    dx={-15}
                    domain={[0, 100]}
                    label={{ value: 'Score (%)', angle: -90, position: 'center', dx: -35, fill: '#374151', fontSize: 13, fontWeight: 700 }}
                  />
                  <ChartTooltip
                    labelFormatter={(label) => `Week ${label}`}
                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,0.1)', padding: '12px' }}
                  />
                  {dashboardData?.weekly_progress?.[0] && Object.keys(dashboardData.weekly_progress[0])
                    .filter(key => !['week_number', 'week_label', 'name', 'start_date', 'end_date', 'status'].includes(key))
                    .map((subject, idx) => (
                      <Area
                        key={subject}
                        type="monotone"
                        dataKey={subject}
                        name={subject}
                        stroke={idx === 0 ? '#667eea' : idx === 1 ? '#764ba2' : idx === 2 ? '#4caf50' : '#ff9800'}
                        strokeWidth={4}
                        fillOpacity={1}
                        fill={`url(#color${idx % 4})`}
                        animationDuration={1500 + (idx * 500)}
                      />
                    ))
                  }
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Overall Stats Card */}
          {dashboardData?.overall_completion && (
            <Card
              sx={{
                p: 1,
                borderRadius: '28px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                boxShadow: '0 15px 35px rgba(102, 126, 234, 0.3)',
                border: 'none',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 3, p: '24px !important' }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={80}
                    thickness={4}
                    sx={{ color: 'rgba(255,255,255,0.15)' }}
                  />
                  <CircularProgress
                    variant="determinate"
                    value={dashboardData.overall_completion.completion_percentage}
                    size={80}
                    thickness={4}
                    sx={{
                      color: 'white',
                      position: 'absolute',
                      left: 0,
                      strokeLinecap: 'round'
                    }}
                  />
                  <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{Math.round(dashboardData.overall_completion.completion_percentage)}%</Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>Overall Learning Progress</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    You've completed <strong>{dashboardData.overall_completion.completed_items}</strong> items out of <strong>{dashboardData.overall_completion.total_items}</strong>. Keep going!
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Section - Latest purchases, Announcements, Actions, House */}
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <DashboardShopCard studentPrefix={studentPrefix} />
          <PackDownloadsDialog
            purchaseId={packDialog?.purchaseId}
            title={packDialog?.title}
            onClose={() => setPackDialog(null)}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: 1 }}>
              <ArticleIcon sx={{ color: "#00695c" }} /> Practice Papers
            </Typography>
            <Button
              component={Link}
              to={`/${studentPrefix}/past-papers`}
              size="small"
              sx={{ fontWeight: 700 }}
            >
              View All
            </Button>
          </Box>
          <Card
            sx={{
              borderRadius: "28px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
              mb: 3,
              border: "1px solid #f0f0f0",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: "16px !important" }}>
              {recentPaperPurchases.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 2.5, px: 1.5 }}>
                  <Avatar sx={{ bgcolor: "#e0f2f1", color: "#00695c", width: 52, height: 52, mx: "auto", mb: 1.5 }}>
                    <ArticleIcon />
                  </Avatar>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#111827", mb: 0.5 }}>
                    Your practice papers will live here
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", fontWeight: 500, mb: 2, lineHeight: 1.6 }}>
                    Real exam-style papers with full mark schemes. When a parent buys one for you, it will appear here ready to start.
                  </Typography>
                  <Button
                    component={Link}
                    to={`/${studentPrefix}/past-papers`}
                    variant="outlined"
                    size="small"
                    sx={{ fontWeight: 700, borderRadius: "999px", textTransform: "none", px: 2.5 }}
                  >
                    Browse practice papers
                  </Button>
                </Box>
              ) : (
                <List disablePadding dense>
                  {recentPaperPurchases.map((row, idx) => {
                    const isPack = row.resource_type === "pdf_pack";
                    const { family, title } = splitPackName(row.resource_name);
                    return (
                      <React.Fragment key={`paper-${row.purchase_id ?? row.paper_id ?? idx}-${idx}`}>
                        {idx > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
                        <ListItemButton
                          onClick={() => handlePastPaperNavigate(row)}
                          sx={{
                            borderRadius: "14px",
                            py: 1.25,
                            px: 1,
                            gap: 1.5,
                            "&:hover": { background: "rgba(0, 137, 123, 0.06)" },
                          }}
                        >
                          <Avatar
                            variant="rounded"
                            sx={{ bgcolor: isPack ? "#ede7f6" : "#e0f2f1", color: isPack ? "#5e35b1" : "#00695c", width: 40, height: 40, borderRadius: "12px" }}
                          >
                            {isPack ? <DownloadIcon fontSize="small" /> : <ArticleIcon fontSize="small" />}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>
                              {title}
                            </Typography>
                            {family ? (
                              <Typography variant="caption" sx={{ color: "#4b5563", fontWeight: 600, display: "block", lineHeight: 1.4 }} noWrap title={family}>
                                {family}
                              </Typography>
                            ) : null}
                            <Typography variant="caption" sx={{ color: "#9ca3af", fontWeight: 600, display: "block" }}>
                              {isPack ? "PDF download" : "Online paper"}
                              {row.purchased_at
                                ? ` · Added ${new Date(row.purchased_at).toLocaleDateString(undefined, { dateStyle: "medium" })}`
                                : ""}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={isPack ? "Download" : "Start"}
                            sx={{ fontWeight: 700, fontSize: "0.7rem", height: 24, bgcolor: "#e0f2f1", color: "#00695c", cursor: "pointer", flexShrink: 0 }}
                          />
                        </ListItemButton>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: 1 }}>
              <ShoppingBagIcon sx={{ color: "#00897b" }} /> Latest purchases
            </Typography>
            <Button component={Link} to={`/${studentPrefix}/mock-exams?tab=exam`} size="small" sx={{ fontWeight: 700 }}>View All</Button>
          </Box>
          <Card
            sx={{
              borderRadius: "28px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
              mb: 3,
              border: "1px solid #f0f0f0",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: "16px !important" }}>
              {latestNonPaperPurchases.length === 0 ? (
                <Typography variant="body2" sx={{ color: "#374151", fontWeight: 600, py: 1, px: 0.5 }}>
                  No recent mock exam or course purchases yet.
                </Typography>
              ) : (
                <List disablePadding dense>
                  {latestNonPaperPurchases.map((row, idx) => (
                    <React.Fragment key={`${row.resource_type}-${row.mock_exam_id ?? row.paper_id ?? row.course_id ?? idx}-${idx}`}>
                      {idx > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
                      <ListItemButton
                        onClick={() => handleLatestPurchaseNavigate(row)}
                        alignItems="flex-start"
                        sx={{
                          borderRadius: "12px",
                          py: 1.25,
                          px: 1,
                          gap: 1,
                          "&:hover": { background: "rgba(0, 137, 123, 0.06)" },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                            <Chip
                              size="small"
                              label={row.resource_type_label || row.resource_type}
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.68rem",
                                height: 22,
                                bgcolor: "#e0f2f1",
                                color: "#00695c",
                              }}
                            />
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>
                              {row.resource_name}
                            </Typography>
                          </Box>
                          {row.purchased_at ? (
                            <Typography variant="caption" sx={{ color: "#95a5a6", fontWeight: 600, display: "block" }}>
                              Purchased{" "}
                              {new Date(row.purchased_at).toLocaleString(undefined, {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </Typography>
                          ) : null}
                        </Box>
                        <ChevronRight sx={{ color: "#374151", mt: 0.25, flexShrink: 0 }} fontSize="small" />
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Announcements Card */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications sx={{ color: "#ff5722" }} /> Announcements
            </Typography>
          </Box>
          <AnnouncementsList role="student" />

          {/* Action Items Table Section */}
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", mt: 4, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ActionIcon sx={{ color: "#9c27b0" }} /> Action Items
          </Typography>
          <Card
            sx={{
              borderRadius: "28px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
              mb: 4,
              border: '1px solid #f0f0f0',
              overflow: 'hidden'
            }}
          >
            {/* The table keeps a usable width and the container scrolls sideways on phones. */}
            <TableContainer>
              <Table size="small" sx={{ minWidth: 520 }}>
                <TableHead sx={{ bgcolor: '#001f3f' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: '#ffffff', py: 1, fontSize: '0.75rem', borderRight: '1px solid rgba(255,255,255,0.2)' }}>No</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#ffffff', py: 1.5, fontSize: '0.75rem', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#ffffff', py: 1.5, fontSize: '0.75rem', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#ffffff', py: 1.5, fontSize: '0.75rem', borderRight: '1px solid rgba(255,255,255,0.2)' }}>Deadline</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: '#ffffff', py: 1.5, fontSize: '0.75rem' }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#95a5a6', borderRight: '1px solid rgba(255,255,255,0.2)' }}>
                      <Box sx={{ opacity: 0.3, mb: 1 }}>
                        <ActionIcon sx={{ fontSize: 32 }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#374151' }}>
                        No items
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          {/* House Standings Card */}
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEventsIcon sx={{ color: "#fcc419" }} /> House Ranking
          </Typography>
          <Card
            sx={{
              borderRadius: "28px",
              boxShadow: "0 15px 35px rgba(0,0,0,0.04)",
              background: "white",
              border: "1px solid #f0f0f0"
            }}
          >
            <CardContent sx={{ p: '24px !important' }}>
              <Stack spacing={3}>
                {MOCK_HOUSE_STANDINGS.map((house, idx) => (
                  <Box key={house.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: house.color,
                            fontSize: '12px',
                            fontWeight: 800,
                            boxShadow: `0 4px 10px ${house.color}30`
                          }}
                        >
                          {house.avatar}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: "#111827" }}>
                          {house.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 900, color: house.color }}>
                        {house.points.toLocaleString()} pts
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(house.points / 1300) * 100}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: '#f1f2f6',
                        '& .MuiLinearProgress-bar': {
                          background: `linear-gradient(90deg, ${house.color}, ${house.color}a0)`
                        }
                      }}
                    />
                  </Box>
                ))}
              </Stack>

              <Button
                fullWidth
                variant="outlined"
                size="small"
                sx={{
                  mt: 3.5,
                  borderRadius: '14px',
                  textTransform: 'none',
                  fontWeight: 800,
                  borderColor: '#eee',
                  color: '#374151',
                  py: 1,
                  "&:hover": { borderColor: '#ddd', bgcolor: '#f8f9fa' }
                }}
              >
                View Detailed Ranking
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Interactive Learning Games shortcut section */}
        <Grid item xs={12} sx={{ mt: 4 }}>
          <Paper
            sx={{
              p: { xs: 1.5, sm: 2, md: 3 },
              borderRadius: "24px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
              border: "1px solid #f0f0f0",
              background: "white",
            }}
          >
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 0 },
              mb: 2,
            }}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 800,
                    color: "#111827",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TestIcon sx={{ color: "#e91e63" }} /> Interactive Learning Games
                  </Box>
                  {myGameScore !== null && (
                    <Box component="span" sx={{ fontWeight: 700, color: "#e91e63", fontSize: "inherit" }}>
                      Your score: {myGameScore.toLocaleString()} pts
                    </Box>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ color: "#374151", mt: 0.5 }}>
                  Pick a subject and practice with fun, timed games.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate("/student/games")}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 700,
                  px: 2.5,
                  alignSelf: { xs: "stretch", sm: "auto" },
                }}
              >
                View all games
              </Button>
            </Box>

            <Grid container spacing={2}>
              {[
                { key: "maths", label: "Maths", color: "#1976d2" },
                { key: "english", label: "English", color: "#c2185b" },
                { key: "verbal-reasoning", label: "Verbal Reasoning", color: "#8e24aa" },
                { key: "non-verbal-reasoning", label: "Non-Verbal Reasoning", color: "#ff9800" },
              ].map((subject) => (
                <Grid item xs={12} sm={6} md={3} key={subject.key}>
                  <Button
                    fullWidth
                    onClick={() => navigate(`/student/games/${subject.key}`)}
                    sx={{
                      py: 1.5,
                      borderRadius: "18px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: subject.color,
                      border: `1px solid ${subject.color}30`,
                      background: `${subject.color}05`,
                      "&:hover": {
                        background: subject.color,
                        color: "white",
                        transform: "translateY(-2px)",
                        boxShadow: `0 10px 20px ${subject.color}40`,
                      },
                      transition: "all 0.25s ease",
                    }}
                  >
                    {subject.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};

// Helper Icon for House
const EmojiEventsIcon = HouseIcon;

/**
 * Which dashboard a student gets.
 *
 * A running mobile plan gets the readiness dashboard; once that plan ends they get the
 * course dashboard instead. The plan lock is what decides, not `useMobileDashboard`
 * alone: that flag is seeded from localStorage so the page can paint before
 * `student/settings` answers, and a student whose plan lapsed since their last visit
 * still has "1" cached. They would land on the mobile dashboard, whose every endpoint
 * then 402s, and only flip to the course one once the request landed.
 *
 * `subscriptionLock` is cached the same way and carries the answer on that first paint,
 * so reading both keeps the two in step. Testers are never locked and keep their manual
 * choice (see useStudentPortalMode).
 *
 * A student with both a course and a live plan switches sides with the toggle in the
 * StudentLayout top bar. Each side has its own lists and scores on the API, so the body
 * remounts on a switch and nothing from the other side stays on screen.
 */
const Dashboard = () => {
  const {
    useMobileDashboard,
    loading,
    isTester,
    testerView,
    subscriptionLock,
    hasCourseEnrollment,
  } = useStudentPortalMode();
  const undecided = loading && localStorage.getItem("student-use-mobile-dashboard") === null;

  if (undecided) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress thickness={5} size={60} sx={{ color: "#764ba2" }} />
      </Box>
    );
  }

  const planEnded = subscriptionLock?.locked === true;
  // No course and no running plan or trial (a lapsed plan keeps the course dashboard and its
  // "plan has ended" banner): the course dashboard would be all empty tiles, so ask them how
  // they want to practise and list what they bought instead. Testers keep their manual view.
  const noPlanNoCourse = !isTester && !useMobileDashboard && !hasCourseEnrollment && !planEnded;
  const body =
    useMobileDashboard && !planEnded ? (
      <MobileStudentDashboard key="mobile" />
    ) : noPlanNoCourse ? (
      <NoPlanDashboard key="no-plan" studentPrefix={process.env.REACT_APP_STUDENT_PREFIX || "student"} />
    ) : (
      <CourseDashboard key="course" subscriptionLock={subscriptionLock} />
    );

  if (!isTester) return body;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
          p: 1,
          borderRadius: "12px",
          bgcolor: "#fff8e1",
          border: "1px solid #ffe082",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 800, color: "#b26a00", mr: 0.5 }}>
          Tester view
        </Typography>
        {[
          { key: "course", label: "Course dashboard" },
          { key: "mobile", label: "Mobile dashboard" },
        ].map((option) => (
          <Chip
            key={option.key}
            size="small"
            label={option.label}
            onClick={() => setTesterDashboardView(option.key)}
            sx={{
              fontWeight: 700,
              bgcolor: testerView === option.key ? "#b26a00" : "#fff",
              color: testerView === option.key ? "#fff" : "#b26a00",
              border: "1px solid #ffe082",
            }}
          />
        ))}
      </Box>
      {body}
    </Box>
  );
};

export default Dashboard;
